import { useState, useCallback } from 'react';
import { Droplets, Check, Info, RotateCw } from 'lucide-react';
import LabBench from '../../LabBench';
import { Dropper, LitmusPaper, StirringRod } from '@/components/lab';
import InfoBox from '../../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, {
  title: string;
  content: string;
  whatHappens: string;
  observe: string[];
  equation?: string;
}> = {
  1: {
    title: 'Preparing the Nickel Solution',
    content: 'Nickel(II) solutions contain [Ni(H₂O)₆]²⁺ complex ions which give them a pale green color. Nickel is a transition metal with partially filled d orbitals, allowing d-d electronic transitions that absorb certain wavelengths of light.',
    whatHappens: 'You are adding 3 mL of Ni²⁺ solution to a clean test tube. The pale green color is characteristic of hydrated nickel(II) ions.',
    observe: [
      'Solution appears pale green',
      'Color is from [Ni(H₂O)₆]²⁺ complex',
      'Solution should be clear, not cloudy'
    ]
  },
  2: {
    title: 'Adding Ammonia (NH₄OH)',
    content: 'In basic solution, ammonia partially coordinates with nickel forming ammonia complexes. The DMG test requires basic conditions (pH > 7) to work properly. Without sufficient basicity, the DMG will not form the characteristic chelate complex.',
    whatHappens: 'Adding ammonia makes the solution basic. You may see a slight color change to blue-green as nickel-ammonia complexes begin to form. The basic environment is essential for the DMG reaction.',
    observe: [
      'Solution may become slightly blue-green',
      'Some cloudiness may appear initially',
      'Solution becomes basic (pH > 7)'
    ]
  },
  3: {
    title: 'Testing pH with Litmus',
    content: 'The DMG test requires basic conditions to work properly. Red litmus paper turning blue confirms that the solution has reached the necessary basic pH. If the solution is not basic enough, the nickel-DMG complex will not form properly.',
    whatHappens: 'When you dip red litmus paper into the solution, it will turn blue, confirming that the solution is basic. This is required before adding the DMG reagent.',
    observe: [
      'Red litmus paper turns blue',
      'Confirms pH > 7 (basic)',
      'Basic pH is required for DMG test'
    ]
  },
  4: {
    title: 'Adding DMG (Dimethylglyoxime)',
    content: 'Dimethylglyoxime (DMG) is a chelating agent that forms a specific complex with nickel. Each DMG molecule coordinates through its two nitrogen atoms, and two DMG molecules wrap around each nickel ion. The resulting complex is a characteristic red precipitate of nickel dimethylglyoximate.',
    whatHappens: 'Adding DMG to the basic nickel solution begins the formation of nickel dimethylglyoximate. Initially, you may see a pink or orange tinge before the full red color develops.',
    observe: [
      'Solution may turn pink or orange initially',
      'This is the beginning of complex formation',
      'Full color develops after stirring'
    ],
    equation: 'Ni²⁺ + 2 DMG → Ni(DMG)2 (chelate complex)'
  },
  5: {
    title: 'Stirring the Solution',
    content: 'Stirring ensures complete mixing of the DMG with the nickel solution. This allows the chelation reaction to complete, forming the nickel dimethylglyoximate precipitate. Without stirring, the reaction may be incomplete or localized.',
    whatHappens: 'As you stir, the nickel-DMG complex fully forms and precipitates. The solution changes from pale pink/orange to a red colour as the precipitate develops.',
    observe: [
      'Color intensifies during stirring',
      'Red precipitate begins to form',
      'Precipitate is fluffy/flocculent'
    ]
  },
  6: {
    title: 'Observing the Nickel-DMG Precipitate',
    content: 'The red colour of Ni(DMG)2 is highly characteristic and diagnostic for nickel. The colour arises from the square planar geometry of the complex and the extended conjugation through the chelate rings. No other common metal produces this colour with DMG.',
    whatHappens: 'You observe the red precipitate of nickel dimethylglyoximate that has formed. This distinctive colour is the confirmatory evidence for nickel ions.',
    observe: [
      'Red precipitate of nickel dimethylglyoximate',
      'Precipitate is fluffy, not granular',
      'Color is very distinctive and diagnostic'
    ]
  },
  7: {
    title: 'Recording the Positive Result',
    content: 'The red precipitate of nickel dimethylglyoximate is a POSITIVE confirmatory test for nickel(II) ions. This test is highly selective - DMG does not produce this colour with any other common cation. It is used in both qualitative and quantitative analysis.',
    whatHappens: 'You record that the test is positive for Ni²⁺. The formation of the red precipitate of nickel dimethylglyoximate is definitive evidence for the presence of nickel ions.',
    observe: [
      'Positive result: red precipitate of nickel dimethylglyoximate confirms Ni²⁺',
      'This test is highly specific to nickel',
      'Record observations in your lab notebook'
    ],
    equation: 'Ni²⁺ + 2 DMG → Ni(DMG)2↓ (red precipitate)'
  }
};

interface Part4NickelTestProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

// Color progression for nickel-DMG reaction
const nickelColors = {
  initial: '#A5D6A7',      // Pale green - hydrated nickel(II)
  basic: '#80CBC4',        // Blue-green - basic nickel
  dmgAdded: '#FFCDD2',     // Pink tint - DMG just added
  final: '#E53935',        // Bright red - nickel dimethylglyoximate
};

export default function Part4NickelTest({ onStepComplete, currentStep = 1 }: Part4NickelTestProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [niSolutionAdded, setNiSolutionAdded] = useState(false);
  const [nh4ohDrops, setNh4ohDrops] = useState(0);
  const [isDropping, setIsDropping] = useState(false);
  const [pHTested, setPHTested] = useState(false);
  const [litmusColor, setLitmusColor] = useState<'red' | 'blue'>('red');
  const [dmgAdded, setDmgAdded] = useState(false);
  const [dmgDrops, setDmgDrops] = useState(0);
  const [isStirring, setIsStirring] = useState(false);
  const [stirred, setStirred] = useState(false);
  const [precipitateObserved, setPrecipitateObserved] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);

  const getCurrentColor = () => {
    if (!niSolutionAdded) return 'transparent';
    if (stirred || precipitateObserved) return nickelColors.final;
    if (dmgDrops > 0) return nickelColors.dmgAdded;
    if (nh4ohDrops >= 5) return nickelColors.basic;
    return nickelColors.initial;
  };

  const getFillLevel = () => {
    if (!niSolutionAdded) return 0;
    return 50 + nh4ohDrops * 2 + dmgDrops * 3;
  };

  const getPrecipitateAmount = () => {
    if (!stirred) return 0;
    return 100;
  };

  const handleAddNiSolution = useCallback(() => {
    setNiSolutionAdded(true);
    if (onStepComplete) onStepComplete('nickel-1');
  }, [onStepComplete]);

  const handleAddNH4OH = useCallback(() => {
    if (niSolutionAdded && nh4ohDrops < 8) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setNh4ohDrops(prev => {
          const newDrops = prev + 1;
          if (newDrops >= 5 && onStepComplete) {
            onStepComplete('nickel-2');
          }
          return newDrops;
        });
      }, 500);
    }
  }, [niSolutionAdded, nh4ohDrops, onStepComplete]);

  const handleTestpH = useCallback(() => {
    if (nh4ohDrops >= 5) {
      setLitmusColor('blue');
      setPHTested(true);
      if (onStepComplete) onStepComplete('nickel-3');
    }
  }, [nh4ohDrops, onStepComplete]);

  const handleAddDMG = useCallback(() => {
    if (pHTested && dmgDrops < 3) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setDmgDrops(prev => {
          const newDrops = prev + 1;
          if (newDrops >= 3) {
            setDmgAdded(true);
            if (onStepComplete) onStepComplete('nickel-4');
          }
          return newDrops;
        });
      }, 500);
    }
  }, [pHTested, dmgDrops, onStepComplete]);

  const handleStir = useCallback(() => {
    if (dmgAdded && !stirred) {
      setIsStirring(true);
      setTimeout(() => {
        setIsStirring(false);
        setStirred(true);
        if (onStepComplete) onStepComplete('nickel-5');
      }, 1500);
    }
  }, [dmgAdded, stirred, onStepComplete]);

  const handleObservePrecipitate = useCallback(() => {
    if (stirred) {
      setPrecipitateObserved(true);
      if (onStepComplete) onStepComplete('nickel-6');
    }
  }, [stirred, onStepComplete]);

  const handleRecordResult = useCallback(() => {
    if (precipitateObserved) {
      setResultRecorded(true);
      if (onStepComplete) onStepComplete('nickel-7');
    }
  }, [precipitateObserved, onStepComplete]);

  return (
    <LabBench
      experimentName="Nickel Confirmation Test"
      completedCount={[niSolutionAdded, nh4ohDrops >= 5, pHTested, dmgAdded, stirred, precipitateObserved, resultRecorded].filter(Boolean).length}
      totalSteps={7}
      equipment={['Ni²⁺ solution', '6M NH₄OH', 'DMG solution', 'Litmus paper', 'Stirring rod']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="nickel-test">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Test Tube with Ni²⁺ solution */}
          <div className="equipment-section">
            <svg viewBox="0 0 70 140" width={70} height={140}>
              <defs>
                <linearGradient id="tube-glass-ni" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
                <linearGradient id="ni-liquid" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={getCurrentColor()} stopOpacity="0.85" />
                  <stop offset="100%" stopColor={getCurrentColor()} stopOpacity="0.95" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="35" cy="15" rx="16" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M19 15 L19 105 Q19 120 35 120 Q51 120 51 105 L51 15" fill="url(#tube-glass-ni)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Liquid fill */}
              {niSolutionAdded && (
                <path
                  d={`M21 ${120 - getFillLevel()} L21 103 Q21 117 35 117 Q49 117 49 103 L49 ${120 - getFillLevel()} Z`}
                  fill="url(#ni-liquid)"
                >
                  <animate
                    attributeName="opacity"
                    values="0.85;0.95;0.85"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </path>
              )}

              {/* Red precipitate forming */}
              {stirred && (
                <g>
                  {/* Main precipitate */}
                  <ellipse cx="35" cy="100" rx="12" ry="5" fill="#E53935" stroke="#C62828" strokeWidth="0.5">
                    <animate attributeName="ry" values="2;5;5" dur="1s" fill="freeze" />
                  </ellipse>
                  {/* Flocculent precipitate particles */}
                  <ellipse cx="28" cy="85" rx="4" ry="2" fill="#EF5350" opacity="0.8">
                    <animate attributeName="cy" values="75;95;95" dur="2s" fill="freeze" />
                  </ellipse>
                  <ellipse cx="42" cy="80" rx="3" ry="1.5" fill="#EF5350" opacity="0.8">
                    <animate attributeName="cy" values="70;92;92" dur="1.8s" fill="freeze" />
                  </ellipse>
                  <ellipse cx="35" cy="90" rx="5" ry="2" fill="#EF5350" opacity="0.9">
                    <animate attributeName="cy" values="60;97;97" dur="2.2s" fill="freeze" />
                  </ellipse>
                </g>
              )}

              {/* Stirring animation */}
              {isStirring && (
                <g>
                  <line x1="35" y1="20" x2="35" y2="95" stroke="#666" strokeWidth="2">
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      values="0 35 70;15 35 70;-15 35 70;0 35 70"
                      dur="0.5s"
                      repeatCount="indefinite"
                    />
                  </line>
                </g>
              )}

              {/* Glass shine */}
              <line x1="23" y1="20" x2="23" y2="95" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">Ni²⁺ Solution</span>
          </div>

          {/* NH4OH Dropper */}
          <div className="equipment-section">
            <Dropper
              label="6M NH₄OH"
              liquidColor="#F5F5F5"
              onClick={niSolutionAdded && !pHTested ? handleAddNH4OH : undefined}
              active={niSolutionAdded && nh4ohDrops < 8 && !pHTested}
              dropping={isDropping && !pHTested}
              disabled={!niSolutionAdded || pHTested}
            />
          </div>

          {/* Litmus Paper */}
          <div className="equipment-section">
            <LitmusPaper
              color={litmusColor}
              onClick={nh4ohDrops >= 5 && !pHTested ? handleTestpH : undefined}
              active={nh4ohDrops >= 5 && !pHTested}
              disabled={nh4ohDrops < 5 || pHTested}
            />
          </div>

          {/* DMG Dropper */}
          <div className="equipment-section">
            <Dropper
              label="DMG"
              liquidColor="#FFFDE7"
              onClick={pHTested && !dmgAdded ? handleAddDMG : undefined}
              active={pHTested && dmgDrops < 3}
              dropping={isDropping && pHTested}
              disabled={!pHTested || dmgAdded}
            />
          </div>

          {/* Stirring Rod */}
          <div className="equipment-section">
            <StirringRod
              onClick={dmgAdded && !stirred ? handleStir : undefined}
              active={dmgAdded && !stirred}
              disabled={!dmgAdded || stirred}
              stirring={isStirring}
            />
          </div>
        </div>

        {/* Status Messages */}
        <div className="status-section">
          {!niSolutionAdded && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 1</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
                >
                  ?
                </button>
              </div>
              <p>Add 3 mL of <strong>Ni²⁺ solution</strong> to a clean test tube.</p>
              <button className="action-btn" onClick={handleAddNiSolution}>
                <Droplets size={16} />
                Add Ni²⁺ Solution
              </button>
            </div>
          )}

          {niSolutionAdded && nh4ohDrops < 5 && !pHTested && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 2</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
                >
                  ?
                </button>
              </div>
              <p>Add <strong>6M NH₄OH dropwise</strong> until basic. ({nh4ohDrops}/5+ drops)</p>
              <p className="hint">Click the 6M NH₄OH dropper to add drops.</p>
            </div>
          )}

          {nh4ohDrops >= 5 && !pHTested && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 3</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(3); }}
                >
                  ?
                </button>
              </div>
              <p>Test pH with <strong>red litmus paper</strong>.</p>
              <p className="hint">Click the litmus paper to test.</p>
            </div>
          )}

          {pHTested && !dmgAdded && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 4</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(4); }}
                >
                  ?
                </button>
              </div>
              <p>Add <strong>3 drops of DMG</strong> (dimethylglyoxime). ({dmgDrops}/3 drops)</p>
              <p className="hint">Click the DMG dropper to add drops.</p>
            </div>
          )}

          {dmgAdded && !stirred && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 5</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(5); }}
                >
                  ?
                </button>
              </div>
              <p><strong>Stir</strong> the solution to mix well.</p>
              <button className="action-btn" onClick={handleStir}>
                <RotateCw size={16} />
                Stir Solution
              </button>
            </div>
          )}

          {stirred && !precipitateObserved && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 6</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(6); }}
                >
                  ?
                </button>
              </div>
              <p>Observe the <strong>red precipitate</strong> of nickel dimethylglyoximate.</p>
              <button className="action-btn" onClick={handleObservePrecipitate}>
                <Check size={16} />
                Observe Precipitate
              </button>
            </div>
          )}

          {precipitateObserved && !resultRecorded && (
            <div className="instruction-card success">
              <div className="instruction-header">
                <span className="step-badge">Step 7</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(7); }}
                >
                  ?
                </button>
              </div>
              <p>Record the <strong>positive result</strong> for nickel confirmation.</p>
              <button className="action-btn success" onClick={handleRecordResult}>
                <Check size={16} />
                Record Result
              </button>
            </div>
          )}

          {resultRecorded && (
            <div className="result-card">
              <Check size={20} />
              <div>
                <p><strong>Ni²⁺ Confirmed!</strong></p>
                <p>Red precipitate of nickel dimethylglyoximate confirms Ni²⁺.</p>
              </div>
            </div>
          )}
        </div>

        {/* pH indicator */}
        {pHTested && (
          <div className="ph-indicator">
            <span>pH Test:</span>
            <span className={`ph-result ${litmusColor}`}>
              Litmus turned {litmusColor} - Solution is BASIC
            </span>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4><Info size={14} /> DMG Chelation - The Nickel Test</h4>
          <p>
            Dimethylglyoxime (DMG) is a chelating agent that forms a characteristic red precipitate
            specifically with nickel ions. The test must be performed in basic solution
            for optimal results.
          </p>
          <div className="equation">
            Ni<sup>2+</sup> + 2 DMG → Ni(DMG)<sub>2</sub> ↓ (red precipitate)
          </div>
          <p className="equation-note">This reaction is highly selective - other cations do not give this color.</p>
        </div>

        <style>{`
          .nickel-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 700px;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .status-section {
            width: 100%;
            min-height: 100px;
          }

          .instruction-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction-card.success {
            border-left-color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction-card p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction-card .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-top: 8px;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .result-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 12px;
            color: #00A86B;
          }

          .result-card p {
            margin: 0;
            color: var(--text-primary);
          }

          .result-card p:first-child {
            color: #00A86B;
            font-weight: 600;
          }

          .ph-indicator {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 13px;
          }

          .ph-result {
            font-weight: 600;
          }

          .ph-result.blue {
            color: #2563EB;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 14px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 10px;
            border-radius: 6px;
            margin-bottom: 8px;
          }

          .equation-note {
            font-size: 11px !important;
            color: var(--text-muted) !important;
            text-align: center;
          }

          .instruction-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp2"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
